import React, { useEffect, useState, useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { curriculumAPI } from '../../services/api';
import useOrgScope, { expandAliases } from '../../hooks/useOrgScope';
import './coach.css';

// Phase 8.5: Per-domain presentation metadata is now consumed from the
// /scenario-os/curriculum/domains API response, which is enriched with
// fields from each pack's pack.meta.yaml (icon, playbook_label,
// reference_links, etc.). The only piece that stays in the frontend is
// the icon-key -> emoji glyph table below — adding a new icon there is
// a one-time generic edit that survives any number of new domains.
//
// New domains automatically appear in the sidebar with the right label,
// icon, playbook terminology, and reference links — no code edits.

const ICON_MAP = {
  football: '🏈',
  brain: '🧠',
  agent: '🤖',
  rocket: '💼',
  shield: '🛡️',
  gamepad: '🎮',
  stethoscope: '🩺',
  cricket: '🏏',
  basketball: '🏀',
  baseball: '⚾',
  graduation: '🎓',
  book: '📚',
  microscope: '🔬',
  chart: '📊',
  briefcase: '💼',
};

function iconFor(key, fallback = '🎯') {
  if (!key) return fallback;
  return ICON_MAP[key] || fallback;
}

function domainNavLinks(dom) {
  const links = [
    { to: `/coach/scene-library/${dom.domain_id}`, icon: '🎬', label: 'Content Library',
      title: 'All teachable assets in this domain: scenes, plays, drills, quizzes.' },
    { to: `/coach/curriculum/${dom.domain_id}`,   icon: '📚', label: 'Curriculum',
      title: 'Tracks → Modules → Scenes. Mastery gates unlock the next module.' },
    { to: `/coach/workspace/${dom.domain_id}/item-bank`, icon: '🗃️', label: 'Item Bank',
      title: 'CAT question pool for this domain — browse, generate, and manage IRT-calibrated items by skill.' },
    { to: `/coach/workspace/${dom.domain_id}/health`, icon: '🩺', label: 'Domain Health',
      title: 'Module coverage, skill gaps, and missing flashcard scenes for this domain.' },
  ];
  if (dom.playbook_enabled !== false) {
    links.push({
      to: `/coach/playbooks/${dom.domain_id}`,
      icon: '📒',
      label: dom.playbook_label || 'Collections',
      title: 'Coach-owned binders of scenes — outside the formal curriculum.',
    });
  }
  return links;
}

function expandReferenceLinks(dom) {
  // {domain_id} placeholder is interpolated server-side or here; we
  // do it here so an old API response without interpolated paths still
  // works.
  return (dom.reference_links || []).map((link) => ({
    ...link,
    to: (link.path || link.to || '').replace('{domain_id}', dom.domain_id),
    icon: iconFor(link.icon, '📌'),
  }));
}

function navAllowed(hasUiAccess, key, fallback) {
  const v = hasUiAccess?.(key);
  if (v === null || v === undefined) return fallback;
  return !!v;
}

export default function CoachLayout({ children, title }) {
  const {
    learner, logout, authoringDomains, isAdmin, isOwner, isPlatformAdmin, hasUiAccess,
  } = useAuth();
  const {
    unrestricted, domains: scopeDomains, loading: scopeLoading,
  } = useOrgScope();
  const navigate = useNavigate();
  const [domainCatalog, setDomainCatalog] = useState([]);

  const showContentStudio = navAllowed(hasUiAccess, 'content_studio', true);
  const showAnalytics = navAllowed(hasUiAccess, 'analytics', true);
  const showDomainBuilder = navAllowed(hasUiAccess, 'domain_builder', isAdmin);
  const showUserMgmt = navAllowed(hasUiAccess, 'user_management', isAdmin);
  const showOrgMgmt = navAllowed(hasUiAccess, 'org_management', isAdmin);
  const showBilling = navAllowed(hasUiAccess, 'billing', isOwner);
  const showRoleMgmt = navAllowed(hasUiAccess, 'role_management', isPlatformAdmin);
  // Platform-only product surfaces — never grant via isAdmin alone
  const showBranding = navAllowed(hasUiAccess, 'branding', isPlatformAdmin);
  const showTenants = navAllowed(hasUiAccess, 'tenants', isPlatformAdmin);
  const showConfigStore = navAllowed(hasUiAccess, 'config_store', isPlatformAdmin);
  const showAilHealth = navAllowed(hasUiAccess, 'ail_health', isPlatformAdmin);
  const showAudit = navAllowed(hasUiAccess, 'audit', isPlatformAdmin);

  // Fetch the domain catalog so we can pick up display_name + ordering
  // from the server's domain pack registry. Falls back to the
  // entitlement list shape if the API is offline.
  useEffect(() => {
    let mounted = true;
    curriculumAPI.listDomains()
      .then(({ data }) => {
        if (!mounted) return;
        setDomainCatalog(Array.isArray(data?.domains) ? data.domains : []);
      })
      .catch(() => setDomainCatalog([]));
    return () => { mounted = false; };
  }, []);

  // Visible domains = scope/me domains when available, else authoringDomains.
  // NEVER treat isAdmin as "all domains". Only platform_admin / unrestricted
  // scope sees the full catalog. Owner / administrator / coach stay scoped.
  const visibleDomains = useMemo(() => {
    const seeAll = isPlatformAdmin || unrestricted;
    if (seeAll) {
      if (domainCatalog.length) return domainCatalog;
      if (scopeDomains.length) return scopeDomains;
      return (authoringDomains || []).map((id) => ({ domain_id: id, display_name: id }));
    }

    // Prefer /admin/scope/me domains (carry display_name + aliases)
    if (!scopeLoading && scopeDomains.length) {
      const allowed = expandAliases(scopeDomains.map((d) => d.domain_id));
      if (domainCatalog.length) {
        const byId = Object.fromEntries(scopeDomains.map((d) => [d.domain_id, d]));
        return domainCatalog
          .filter((d) => allowed.has(d.domain_id))
          .map((d) => ({
            ...d,
            display_name: byId[d.domain_id]?.display_name || d.display_name || d.domain_id,
          }));
      }
      return scopeDomains;
    }

    // Fallback: JWT authoring_domains (+ AI alias expand)
    const allowed = expandAliases(authoringDomains || []);
    if (!domainCatalog.length) {
      return [...allowed].map((id) => ({ domain_id: id, display_name: id }));
    }
    return domainCatalog.filter((d) => allowed.has(d.domain_id));
  }, [domainCatalog, authoringDomains, isPlatformAdmin, unrestricted, scopeDomains, scopeLoading]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="coach-portal">
      <aside className="coach-sidebar">
        <div className="coach-brand">
          <img
            src="/teachiq-logo.png"
            alt="TeachIQ"
            style={{ height: 40, width: 40, marginRight: 8, objectFit: 'cover', borderRadius: 4 }}
          />
          <span className="coach-brand-name">TeachIQ</span>
          <span className="coach-brand-badge">Coach</span>
        </div>

        <nav className="coach-nav">
          {/* Cross-domain entries — always shown for any coach role */}
          <NavLink to="/coach" end className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}>
            <span className="nav-icon">📊</span> Dashboard
          </NavLink>
          <NavLink to="/coach/learners" className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}>
            <span className="nav-icon">👥</span> My Learners
          </NavLink>
          <NavLink
            to="/coach/rosters"
            className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
            title="Named groups of learners — teams, classes, cohorts — used to bulk-assign lessons and curricula."
          >
            <span className="nav-icon">🗂</span> Cohorts
          </NavLink>
          <NavLink
            to="/coach/chats"
            className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
            title="Monitor learner ↔ AI coach chat sessions and send direct replies."
          >
            <span className="nav-icon">💬</span> Chats
          </NavLink>
          {showContentStudio && (
          <NavLink
            to="/coach/content-studio"
            className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
            title="Create curriculum content through AI-powered conversation. Generate modules, scenarios, quizzes, and more."
          >
            <span className="nav-icon">🏗️</span> Content Studio
          </NavLink>
          )}
          <NavLink
            to="/coach/announcements"
            className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
            title="Send announcements to enrolled learners or specific cohorts."
          >
            <span className="nav-icon">📢</span> Announcements
          </NavLink>
          <NavLink
            to="/coach/moderation"
            className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
            title="Review and moderate learner discussion posts."
          >
            <span className="nav-icon">🛡️</span> Moderation
          </NavLink>
          <NavLink
            to="/coach/submissions"
            className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
            title="All submissions — projects, capstones, code labs. Filter by type and status; approve or request resubmission when configured."
          >
            <span className="nav-icon">📋</span> All Submissions
          </NavLink>
          {showAnalytics && (
          <NavLink
            to="/coach/analytics"
            className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
            title="Learner metrics, completion rates, mastery analytics, and content effectiveness."
          >
            <span className="nav-icon">📈</span> Analytics
          </NavLink>
          )}
          <NavLink
            to="/coach/flashcards"
            className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
            title="Manage flashcard scenes by domain and module. View module coverage, edit cards, and assign to learners."
          >
            <span className="nav-icon">🃏</span> Flashcards
          </NavLink>
          {showAnalytics && (
          <>
          <NavLink
            to="/coach/analytics-dashboard"
            className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
            title="Deep-dive analytics: at-risk learners, scene heatmaps, overlay efficacy, confusion clusters."
          >
            <span className="nav-icon">🔬</span> Analytics (Advanced)
          </NavLink>
          <NavLink
            to="/coach/usage"
            className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
            title="Usage reports: time spent, tokens, cost by domain/module/scene type/learner. Export XLSX/PDF."
          >
            <span className="nav-icon">⏱</span> Usage
          </NavLink>
          </>
          )}
          {showDomainBuilder && (
            <NavLink
              to="/coach/workspace-builder"
              className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
              title="Platform admin only — scaffold a new domain pack through an AI-powered multi-step wizard."
            >
              <span className="nav-icon">🏗</span> Domain Builder
            </NavLink>
          )}

          {/* Per-domain blocks — driven entirely by authoringDomains.
              Coach Casey only ever sees the football group; AI Coach
              only sees ai_orchestrator + ai_entrepreneur.  An admin
              sees every domain the registry knows about.

              Phase 8.5: every per-domain field (icon, label, playbook
              terminology, reference links) is consumed from the API
              row, which is itself derived from pack.meta.yaml. Adding
              a new domain creates the sidebar entry automatically. */}
          {visibleDomains.map((dom) => {
            const groupLabel = dom.display_name || dom.domain_id;
            const groupIcon = iconFor(dom.icon);
            const refLinks = expandReferenceLinks(dom);
            return (
              <React.Fragment key={dom.domain_id}>
                <div className="coach-nav-section-label">
                  {groupIcon} {groupLabel}
                </div>
                {domainNavLinks(dom).map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
                    title={link.title}
                  >
                    <span className="nav-icon">{link.icon}</span> {link.label}
                  </NavLink>
                ))}
                {/* Per-domain reference catalogs (e.g. football's
                    Coverage Library + Positions / Roles). Empty for
                    domains whose manifest doesn't declare any. */}
                {refLinks.length ? (
                  <>
                    <div className="coach-nav-section-label" style={{ opacity: 0.7 }}>Reference</div>
                    {refLinks.map((link) => (
                      <NavLink
                        key={link.to}
                        to={link.to}
                        className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
                        title={link.title}
                      >
                        <span className="nav-icon">{link.icon}</span> {link.label}
                      </NavLink>
                    ))}
                  </>
                ) : null}
              </React.Fragment>
            );
          })}

          {/* ── Tenant admin section (platform_admin / ui_access only) ─ */}
          {(showBranding || showTenants) && (
            <>
              <div className="coach-nav-section-label">⚙️ Tenant Admin</div>
              {showBranding && (
              <NavLink
                to="/coach/admin/branding"
                className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
                title="Per-tenant logo, colour palette, hero tagline, email from-address. Lives in config_store."
              >
                <span className="nav-icon">🎨</span> Branding
              </NavLink>
              )}
              {showTenants && (
              <NavLink
                to="/coach/admin/tenants"
                className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
                title="Canonical list of registered tenants (display name, tier, owner)."
              >
                <span className="nav-icon">🏷️</span> Tenants
              </NavLink>
              )}
            </>
          )}
          {(showUserMgmt || showOrgMgmt || showBilling || showRoleMgmt || showConfigStore) && (
            <>
              <div className="coach-nav-section-label">👥 User Management</div>
              {showUserMgmt && (
              <NavLink
                to="/coach/admin/users"
                className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
                title="Manage learner accounts — edit profiles, reset passwords, assign tracks, bulk import."
              >
                <span className="nav-icon">👤</span> Users
              </NavLink>
              )}
              {showOrgMgmt && (
              <NavLink
                to="/coach/admin/organizations"
                className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
                title="Org hierarchy — create and manage Organizations, Groups, and Sub-groups."
              >
                <span className="nav-icon">🏢</span> Organizations
              </NavLink>
              )}
              {showRoleMgmt && (
              <NavLink
                to="/coach/admin/roles"
                className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
                title="Role definitions and ui_access — which coach pages each role can see."
              >
                <span className="nav-icon">🪪</span> Role Management
              </NavLink>
              )}
              {showConfigStore && (
              <NavLink
                to="/coach/admin/config-store"
                className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
                title="TeachIQ product config_store — domain scene types, role defs, analytics thresholds."
              >
                <span className="nav-icon">⚙️</span> Config Store
              </NavLink>
              )}
              {showBilling && (
                <NavLink
                  to="/coach/admin/billing"
                  className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
                  title="Invoice management — generate, download, and email monthly invoices."
                >
                  <span className="nav-icon">💳</span> Billing
                </NavLink>
              )}
            </>
          )}
          {(showAilHealth || showAudit) && (
            <>
              <div className="coach-nav-section-label">🔧 Platform Admin</div>
              {showAilHealth && (
              <NavLink
                to="/coach/platform/ail-health"
                className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
                title="AIL system health: collection counts, agent mastery seeding, BKT validation metrics. Platform admins only."
              >
                <span className="nav-icon">📡</span> AIL Health
              </NavLink>
              )}
              {showAudit && (
              <NavLink
                to="/coach/admin/audit"
                className={({ isActive }) => `coach-nav-item ${isActive ? 'active' : ''}`}
                title="Platform-level audit log — all admin actions, config changes, and agent promotions."
              >
                <span className="nav-icon">🔍</span> Audit Log
              </NavLink>
              )}
            </>
          )}
        </nav>

        <div className="coach-sidebar-footer">
          <div className="coach-user">
            <span className="coach-user-name">{learner?.display_name}</span>
            <span className="coach-user-role">{isAdmin ? 'Admin' : 'Coach'}</span>
          </div>
          <button className="coach-logout-btn" onClick={handleLogout}>Sign Out</button>
        </div>
      </aside>

      <main className="coach-main">
        {title && (
          <div className="coach-page-header">
            <h1 className="coach-page-title">{title}</h1>
          </div>
        )}
        <div className="coach-page-body">
          {children}
        </div>
      </main>
    </div>
  );
}
